import { Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { loadConfig, saveConfig, type TimerConfig } from "@/lib/config";
import { notify } from "@/lib/notification";
import { useState } from "react";

function SettingRow({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div className="flex items-center justify-between gap-4">
			<Label className="text-[0.6875rem] font-semibold uppercase tracking-[0.15em] text-muted-foreground">
				{label}
			</Label>
			{children}
		</div>
	);
}

function Checkbox({ checked, onChange }: { checked: boolean; onChange: (checked: boolean) => void }) {
	return (
		<button
			type="button"
			role="checkbox"
			aria-checked={checked}
			onClick={() => onChange(!checked)}
			className={`size-5 rounded border-2 transition-colors ${
				checked ? "border-ember bg-ember" : "border-border bg-transparent"
			}`}
		>
			{checked && (
				<svg viewBox="0 0 12 12" fill="none" className="text-white">
					<path
						d="M2 6L5 9L10 3"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			)}
		</button>
	);
}

export function Config({ onClose, onSave }: { onClose: () => void; onSave?: () => void }) {
	const [config, setConfig] = useState<TimerConfig>(() => loadConfig());
	const [saved, setSaved] = useState(false);

	function handleSave() {
		saveConfig(config);
		setSaved(true);
		setTimeout(() => setSaved(false), 2000);
		onSave?.();
	}

	const movementsByCategory = {
		strength: config.movements.filter((m) => m.category === "strength").length,
		walk: config.movements.filter((m) => m.category === "walk").length,
		stretch: config.movements.filter((m) => m.category === "stretch").length,
		mobility: config.movements.filter((m) => m.category === "mobility").length,
	};

	return (
		<div className="w-full max-w-xs rounded-2xl border bg-card px-8 py-10">
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-2">
					<Settings className="size-4 text-muted-foreground" />
					<h2 className="text-[0.6875rem] font-semibold uppercase tracking-[0.25em] text-muted-foreground">
						Settings
					</h2>
				</div>
				<button
					onClick={onClose}
					className="text-xs text-muted-foreground hover:text-foreground transition-colors"
				>
					Close
				</button>
			</div>

			<div className="mt-8 space-y-6">
				{/* Timer Durations */}
				<section className="space-y-4">
					<h3 className="text-xs font-medium text-foreground">Timer Durations</h3>

					<SettingRow label="Focus">
						<div className="flex items-center gap-2">
							<Input
								type="number"
								min="1"
								max="90"
								value={config.durationMins}
								onChange={(e) =>
									setConfig({ ...config, durationMins: parseInt(e.target.value) || 1 })
								}
								className="h-8 w-16 text-center text-sm"
							/>
							<span className="text-xs text-muted-foreground">min</span>
						</div>
					</SettingRow>

					<SettingRow label="Short Break">
						<div className="flex items-center gap-2">
							<Input
								type="number"
								min="1"
								max="30"
								value={config.shortBreakMins}
								onChange={(e) =>
									setConfig({ ...config, shortBreakMins: parseInt(e.target.value) || 1 })
								}
								className="h-8 w-16 text-center text-sm"
							/>
							<span className="text-xs text-muted-foreground">min</span>
						</div>
					</SettingRow>

					<SettingRow label="Movement Break">
						<div className="flex items-center gap-2">
							<Input
								type="number"
								min="1"
								max="30"
								value={config.movementBreakMins}
								onChange={(e) =>
									setConfig({ ...config, movementBreakMins: parseInt(e.target.value) || 1 })
								}
								className="h-8 w-16 text-center text-sm"
							/>
							<span className="text-xs text-muted-foreground">min</span>
						</div>
					</SettingRow>

					<SettingRow label="Movement Frequency">
						<div className="flex items-center gap-2">
							<Input
								type="number"
								min="1"
								max="10"
								value={config.movementBreakFrequency}
								onChange={(e) =>
									setConfig({ ...config, movementBreakFrequency: parseInt(e.target.value) || 1 })
								}
								className="h-8 w-16 text-center text-sm"
							/>
							<span className="text-xs text-muted-foreground">cycles</span>
						</div>
					</SettingRow>
				</section>

				{/* Sound */}
				<section className="space-y-4">
					<h3 className="text-xs font-medium text-foreground">Notifications</h3>

					<SettingRow label="Sound">
						<Checkbox
							checked={config.soundEnabled}
							onChange={(checked) => setConfig({ ...config, soundEnabled: checked })}
						/>
					</SettingRow>

					<div className="flex gap-2">
						<Button
							onClick={() => notify("Focus", "Back to work", !config.soundEnabled, 'focus')}
							variant="outline"
							size="sm"
							className="flex-1 text-xs"
						>
							Focus
						</Button>
						<Button
							onClick={() => notify("Break", "Time to rest", !config.soundEnabled, 'break')}
							variant="outline"
							size="sm"
							className="flex-1 text-xs"
						>
							Break
						</Button>
						<Button
							onClick={() => notify("Movement", "Get up and move", !config.soundEnabled, 'movement')}
							variant="outline"
							size="sm"
							className="flex-1 text-xs"
						>
							Move
						</Button>
					</div>
				</section>

				{/* Movements */}
				<section className="space-y-4">
					<h3 className="text-xs font-medium text-foreground">Movement Pool</h3>

					<div className="rounded-lg border bg-muted/30 px-4 py-3 space-y-2">
						<div className="flex items-center justify-between text-xs">
							<span className="text-muted-foreground">Strength</span>
							<span className="font-medium tabular-nums">{movementsByCategory.strength}</span>
						</div>
						<div className="flex items-center justify-between text-xs">
							<span className="text-muted-foreground">Walk</span>
							<span className="font-medium tabular-nums">{movementsByCategory.walk}</span>
						</div>
						<div className="flex items-center justify-between text-xs">
							<span className="text-muted-foreground">Stretch</span>
							<span className="font-medium tabular-nums">{movementsByCategory.stretch}</span>
						</div>
						<div className="flex items-center justify-between text-xs">
							<span className="text-muted-foreground">Mobility</span>
							<span className="font-medium tabular-nums">{movementsByCategory.mobility}</span>
						</div>
						<div className="border-t pt-2 mt-2 flex items-center justify-between text-xs">
							<span className="font-medium text-foreground">Total</span>
							<span className="font-semibold tabular-nums">{config.movements.length}</span>
						</div>
					</div>
				</section>
			</div>

			<div className="mt-8 flex flex-col items-center gap-2">
				<Button
					onClick={handleSave}
					size="lg"
					className="w-full bg-ember text-white hover:bg-ember/85 transition-colors duration-300"
				>
					{saved ? "Saved!" : "Save Settings"}
				</Button>
			</div>
		</div>
	);
}
